import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import { radiusSm, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import { forwardRef } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "../tooltip/Tooltip"

interface ExteriorColorTileProps {
	className?: string // set by emotion when extended via styled(ExteriorColorTile)
	color: string
	glossy?: boolean
	label?: string
	size?: "large"
	tooltip?: string
}

const clampWhite = (color: string) => (color === "#ffffff" ? "#efefef" : color)

export const ExteriorColorTile = forwardRef<HTMLDivElement, ExteriorColorTileProps>(
	({ className, color, glossy = true, label, size, tooltip }: ExteriorColorTileProps, ref) => {
		const displayColor = clampWhite(color)

		return label ? (
			<Wrapper ref={ref} className={className}>
				<PreviewContainer
					className={!label ? className : ""}
					$color={displayColor}
					$size={size}
					$glossy={glossy}
				/>
				<Tooltip content={tooltip}>
					<Label color="inherit" ellipsis>
						{label}
					</Label>
				</Tooltip>
			</Wrapper>
		) : (
			<PreviewContainer
				ref={ref}
				className={!label ? className : ""}
				$color={displayColor}
				$size={size}
				$glossy={glossy}
			/>
		)
	},
)
ExteriorColorTile.displayName = "ExteriorColorTile"

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const PreviewContainer = styled("div", { shouldForwardProp })<{
	$color: string
	$glossy?: boolean
	$size?: "large"
}>`
	position: relative;
	overflow: hidden;
	display: flex;
	flex-grow: 0;
	flex-shrink: 0;
	background: ${(props) => props.$color};
	width: ${({ $size }) => ($size === "large" ? "36px" : "24px")};
	height: ${({ $size }) => ($size === "large" ? "36px" : "24px")};
	border-radius: ${radiusSm};

	${(props) =>
		props.$glossy &&
		css`
			&:after {
				content: "";
				position: absolute;
				inset: 0;
				background: linear-gradient(
					180deg,
					rgba(255, 255, 255, 0.36) 0%,
					rgba(255, 255, 255, 0.13) 50%,
					rgba(0, 0, 0, 0.12) 52%,
					rgba(0, 0, 0, 0) 100%
				);
			}
		`}
`

const Label = styled(PText)`
	min-width: 0;
`
